// Tournaments: the home page card, starting and joining, and the bracket.
import type { Champion, Crown, LineupPick, PairingView, Player, TournamentPage, TournamentView } from '../src/api.js';
import { match, P } from 'ts-pattern';
import { Lineups } from './lineups.js';
import { Page } from './page.js';
import { Session } from './session.js';
import { type Html, Ui } from './ui.js';

const { api, h, setHtml, crownHtml } = Ui;

const noServerHtml = h`<h3>Your server isn't on Shobu Arena</h3>
  <p class="muted flush">Tournaments happen inside Discord servers, and none of yours is playing right now.
  Duels with your friends and training still work.</p>`;

/** The name, crowned if it's the champion's. */
const nameHtml = (name: string, crowned: Crown | null): Html =>
  name === crowned?.name ? h`${name} ${crownHtml(crowned)}` : h`${name}`;

/** Fetches the tournament page and keeps the nav badge in step with it. */
const fetchPage = async (): Promise<TournamentPage> => {
  const page = await api<TournamentPage>('/api/tournament');
  const t = page.tournament;
  Session.setBadges({ canJoinTournament: t?.status === 'joining' && !t.yourLineup });
  return page;
};

/** The sign-up countdown in `#cd`. Call the returned tick right after re-rendering it. */
const joinCountdown = (page: () => TournamentPage | null): (() => void) =>
  Page.countdown('#cd', () => page()?.tournament?.joinUntil ?? 0);

const cardHtml = (t: TournamentView | null): Html =>
  match(t)
    .with(
      { status: 'joining' },
      joining => h`<div class="muted">Sign-ups close in</div><div class="countdown" id="cd"></div>
      <p class="muted flush">${joining.entrants.length} joined${joining.yourLineup ? ', you included' : ''}.</p>
      <div><a class="btn small" href="#/tournament">${joining.yourLineup ? 'Tournament' : 'Join'}</a></div>`,
    )
    .with(
      { status: 'running' },
      ({ current: f, crowned }) => h`<div class="muted">Tournament</div><h3 class="status-live">Live now</h3>
      ${f ? h`<p class="flush"><b>${nameHtml(f.a ?? '', crowned)}</b> <span class="muted">vs</span> <b>${nameHtml(f.b ?? '', crowned)}</b></p>` : ''}
      <div class="actions">${f ? h`<a class="btn small" href="/match.html?id=${f.matchId}">Watch</a>` : ''}<a class="btn ghost small" href="#/tournament">Bracket</a></div>`,
    )
    .otherwise(
      () => h`<div class="muted">Tournament</div><h3>Nothing on right now</h3>
    <p class="muted flush">Start one and everyone has five minutes to join.</p>
    <div class="actions"><a class="btn small" href="#/tournament">Start a tournament</a><a class="btn ghost small" href="#/lineup">Edit lineup</a></div>`,
    );

const championHtml = (champion: Champion | null): Html =>
  champion
    ? h`<h3>Reigning champion</h3><div class="champions">${champion.name} ${crownHtml(champion)}</div>
      <p class="muted flush">Won the last tournament with</p>
      <div class="champion-lineup">${Lineups.previewHtml(champion.lineup)}</div>`
    : h`<h3>Reigning champion</h3><p class="muted">Nobody has won a tournament yet. Be the first.</p>`;

const pairingStatusText: Record<Exclude<PairingView['status'], 'bye'>, string> = {
  upcoming: 'Up next',
  live: 'Live now',
  done: 'Finished',
};

const pairingHtml = (p: PairingView, yourName: string, crowned: Crown | null): Html => {
  const side = (name: string | null) => {
    const result = p.winner && p.status !== 'bye' ? (p.winner === name ? 'win' : 'lose') : '';
    const placeholder = p.status === 'bye' ? 'bye' : 'tbd';
    return h`<div class="p ${result} ${name && name === yourName ? 'you' : ''} ${name ? '' : placeholder}">${name ? nameHtml(name, crowned) : p.status === 'bye' ? 'Bye' : 'TBD'}</div>`;
  };
  const when = match(p)
    .with({ status: 'upcoming', startAt: P.number }, ({ startAt }) => Page.formatTime(startAt))
    .with({ a: P.string, status: P.not('bye') }, ({ status }) => pairingStatusText[status])
    .otherwise(() => '');
  return h`<div class="bm">
    ${side(p.a)}${side(p.b)}
    <div class="meta"><span class="${p.status === 'live' ? 'status-live' : ''}">${when}</span>
      ${p.matchId ? h`<a href="/match.html?id=${p.matchId}">${p.status === 'done' ? 'Replay' : 'Watch'}</a>` : ''}</div>
  </div>`;
};

const bracketHtml = (t: TournamentView, yourName: string): Html =>
  h`<div class="bracket">${t.rounds.map(
    r =>
      h`<div class="bracket-round"><h3>${r.name}</h3><div class="bracket-slots">${r.pairings.map(p => pairingHtml(p, yourName, t.crowned))}</div></div>`,
  )}</div>`;

const defendingHtml = (crowned: Crown | null): Html | '' =>
  crowned ? h`<p class="defending">${crowned.name} ${crownHtml(crowned)} is defending the crown.</p>` : '';

const headingHtml = (t: TournamentView | null, title: Html | string): Html =>
  match(t)
    .with(
      { status: 'joining' },
      joining => h`<div class="row tight heading-row"><h2>${title}</h2>
          <div class="muted">Sign-ups close in <span class="countdown inline" id="cd"></span></div></div>
          <p class="muted">${joining.startedBy} started a tournament. Lose once and you're out. With an odd bracket the top seeds get a bye in round one.</p>
          ${defendingHtml(joining.crowned)}`,
    )
    .with(
      { status: 'running' },
      running =>
        h`<h2>${title}</h2><p class="muted">Fights run one at a time, so everyone watches the same one. Later rounds fill in once the round before is over.</p>
          ${defendingHtml(running.crowned)}`,
    )
    .with(
      { status: 'finished', champion: P.string },
      finished => h`<h2>${title}</h2><div class="panel stack"><div class="muted">Last tournament</div>
          <div class="champions">${nameHtml(finished.champion, finished.crowned)}</div><p class="muted flush">${finished.runnerUp ?? ''} took second.</p></div>`,
    )
    .otherwise(() => h`<h2>${title}</h2>`);

const currentFightHtml = (t: TournamentView | null): Html | '' => {
  const next = t?.current;
  if (!next) return '';
  const name = (side: string | null) => nameHtml(side ?? '', t.crowned);
  const live = next.status === 'live';
  return h`<section class="panel stack"><div class="muted">${live ? 'Playing now' : 'Up next'}</div>
          <div class="list"><div class="list-item"><div class="grow">${name(next.a)} <span class="muted">vs</span> ${name(next.b)}
            <div class="muted sub ${live ? 'status-live' : ''}">${next.status === 'upcoming' && next.startAt ? `Starts ${Page.formatTime(next.startAt)}` : 'Live now'}</div></div>
            <a class="btn small" href="/match.html?id=${next.matchId ?? ''}">Watch</a></div></div></section>`;
};

const entrantsHtml = (t: TournamentView | null, you: string): Html | '' =>
  t?.status === 'joining'
    ? h`<section class="stack"><h3>In the tournament (${t.entrants.length})</h3><div class="list">${t.entrants.map(
        e =>
          h`<div class="list-item">${Page.avatarHtml(e.avatar)}<div class="grow">${nameHtml(e.name, t.crowned)}${e.id === you ? h` <span class="muted sub">(you)</span>` : ''}</div></div>`,
      )}</div></section>`
    : '';

/** Lets the player pick a lineup and shows it next to the button, since joining locks it in for the whole tournament. */
const enterHtml = (player: Player, action: 'start' | 'join'): Html => {
  const picked = Session.activeLineup(player);
  const label = picked ? `${action === 'start' ? 'Start and join' : 'Join'} with ${picked.name}` : 'Join';
  return h`<section class="panel stack">
    <h3>${action === 'start' ? 'Start a tournament' : 'Join the tournament'}</h3>
    <p class="muted flush">${action === 'start' ? 'Everyone gets five minutes to join, or start it sooner yourself. ' : ''}${
      player.lineups.length > 1 ? 'Pick the lineup to enter with. ' : 'You enter with the lineup below. '
    }It stays locked until the tournament is over.</p>
    ${Lineups.pickerHtml(player)}
    <div class="actions">
      <button class="btn small" id="${action}" ${Session.hasPlayableLineup(player) ? '' : 'disabled'}>${label}</button>
      <a class="btn ghost small" href="#/lineup">Edit lineups</a>
    </div>
  </section>`;
};

/** The host can skip the rest of the sign-up countdown. */
const startNowHtml = (t: TournamentView): Html => {
  const ready = t.entrants.length >= 2;
  return h`<div class="actions"><button class="btn small" id="startNow" ${ready ? '' : 'disabled'}>Start now</button>
    <span class="muted sub">${ready ? 'Closes sign-ups and the fights begin.' : 'Waiting for someone to join.'}</span></div>`;
};

const entryHtml = (player: Player, t: TournamentView | null) =>
  match(t)
    .with({ status: 'joining', yourLineup: null }, () => enterHtml(player, 'join'))
    .with(
      { status: 'joining' },
      entered => h`<section class="panel stack"><h3>You're in</h3><p class="muted flush">This is the lineup you'll fight with, whatever you change later.</p>
          ${Lineups.previewHtml(entered.yourLineup)}${entered.hosting ? startNowHtml(entered) : ''}</section>`,
    )
    .with({ status: 'running' }, () => '')
    .otherwise(() => enterHtml(player, 'start'));

const resultsHtml = (t: TournamentView | null, yourName: string): Html | '' =>
  t && t.status !== 'joining' && t.status !== 'cancelled' ? bracketHtml(t, yourName) : '';

const view = async (player: Player): Promise<void> => {
  if (!player.serverId) {
    setHtml(Page.view, h`<h2>Tournament</h2><section class="panel stack">${noServerHtml}</section>`);
    return;
  }
  let page: TournamentPage | null = null;
  const tick = joinCountdown(() => page);

  const draw = () => {
    const t = page?.tournament ?? null;
    const title =
      page && player.servers.length > 1 ? h`Tournament <span class="muted">· ${page.server.name}</span>` : 'Tournament';
    const body = h`${headingHtml(t, title)}<div class="stack">${currentFightHtml(t)}${entryHtml(player, t)}${entrantsHtml(t, player.user.id)}${resultsHtml(t, player.user.name)}</div>`;
    if (!Page.renderIfChanged(Page.view, body)) return;
    tick();
    bindEntry('start', '/api/tournament');
    bindEntry('join', '/api/tournament/join');
    bindEntry('startNow', '/api/tournament/start');
    Lineups.bindChips(Page.view, id => {
      Session.setActiveLineup(id);
      draw();
    });
  };
  const load = async () => {
    page = await fetchPage();
    draw();
  };
  const bindEntry = (action: 'start' | 'join' | 'startNow', path: string) => {
    const btn = document.querySelector<HTMLButtonElement>(`#${action}`);
    if (!btn) return;
    btn.onclick = () =>
      Page.busy(btn, async () => {
        const body: LineupPick = { lineupId: Session.activeLineup(player)?.id };
        await api(path, { method: 'POST', body });
        await load();
      });
  };

  await load();
  Page.poll(load, 3000);
};

export const Tournament = { noServerHtml, fetchPage, joinCountdown, cardHtml, championHtml, view };
